"use client";

import { usePathname } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";

type WeddingEntranceMode = "auto" | "force-on" | "force-off";

type SiteSettingsResponse = {
  weddingEntranceMode: WeddingEntranceMode;
  ceremonyRevealTime: string;
  shouldShowEntrance: boolean;
  error?: string;
};

const ENTRANCE_SEEN_KEY = "johnMaxineWeddingEntranceSeen";
const FULL_ANIMATION_MS = 6200;
const REDUCED_MOTION_MS = 1500;
const MAX_INTRO_MS = 8000;

function isReplayRequest() {
  try {
    return new URLSearchParams(window.location.search).get("entranceReplay") === "1";
  } catch {
    return false;
  }
}

function getPrefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export default function WeddingEntranceGate() {
  const pathname = usePathname();
  const [isEligible, setIsEligible] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const completeEntrance = useCallback(() => {
    try {
      sessionStorage.setItem(ENTRANCE_SEEN_KEY, "1");
    } catch {}

    setIsVisible(false);
  }, []);

  useEffect(() => {
    let active = true;

    if (pathname !== "/") return;

    async function loadSettings() {
      try {
        const response = await fetch("/api/site-settings", { cache: "no-store" });
        const settings = (await response.json()) as SiteSettingsResponse;

        if (!active) return;

        if (!response.ok || settings.error || settings.weddingEntranceMode === "force-off" || !settings.shouldShowEntrance) {
          setIsEligible(false);
          setIsVisible(false);
          return;
        }

        const forceOn = settings.weddingEntranceMode === "force-on";
        const replayRequested = isReplayRequest();
        const alreadySeen = sessionStorage.getItem(ENTRANCE_SEEN_KEY) === "1";

        setIsEligible(true);

        if (forceOn || replayRequested || !alreadySeen) {
          setPrefersReducedMotion(getPrefersReducedMotion());
          setAnimationKey((key) => key + 1);
          setIsVisible(true);
        }
      } catch (error) {
        console.error(error);
      }
    }

    void loadSettings();

    return () => {
      active = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (!isVisible) return;

    const finishTimer = window.setTimeout(
      completeEntrance,
      prefersReducedMotion ? REDUCED_MOTION_MS : FULL_ANIMATION_MS
    );
    const failsafeTimer = window.setTimeout(completeEntrance, MAX_INTRO_MS);

    return () => {
      window.clearTimeout(finishTimer);
      window.clearTimeout(failsafeTimer);
    };
  }, [completeEntrance, isVisible, prefersReducedMotion]);

  function replayEntrance() {
    setPrefersReducedMotion(getPrefersReducedMotion());
    setAnimationKey((key) => key + 1);
    setIsVisible(true);
  }

  if (pathname !== "/" || !isEligible) return null;

  return (
    <>
      {isVisible ? (
        <div
          key={animationKey}
          className="wedding-entrance-overlay"
          data-reduced-motion={prefersReducedMotion ? "true" : "false"}
          role="dialog"
          aria-label="Mr and Mrs wedding entrance animation"
          aria-modal="false"
        >
          <style jsx>{`
            .wedding-entrance-overlay {
              position: fixed;
              inset: 0;
              z-index: 80;
              display: grid;
              place-items: center;
              overflow: hidden;
              background:
                radial-gradient(circle at 50% 30%, rgba(255, 250, 240, 0.96), rgba(246, 244, 239, 0.98) 45%, rgba(233, 223, 198, 0.98)),
                #f6f4ef;
              color: #1f3f6d;
              animation: entrance-shell-fade 900ms ease 5.25s forwards;
              contain: layout paint style;
            }

            .entrance-skip {
              position: fixed;
              right: max(14px, env(safe-area-inset-right));
              top: max(14px, env(safe-area-inset-top));
              z-index: 5;
              min-height: 46px;
              border: 1px solid rgba(202, 162, 58, 0.64);
              border-radius: 999px;
              background: rgba(255, 250, 240, 0.9);
              color: #1f4e79;
              cursor: pointer;
              font-size: 0.78rem;
              font-weight: 800;
              letter-spacing: 0.16em;
              padding: 0 18px;
              text-transform: uppercase;
              box-shadow: 0 14px 34px rgba(31, 78, 121, 0.12);
              -webkit-tap-highlight-color: transparent;
            }

            .entrance-stage {
              position: relative;
              width: min(92vw, 520px);
              height: min(78dvh, 720px);
              min-height: 520px;
              overflow: hidden;
              border: 1px solid rgba(202, 162, 58, 0.48);
              border-radius: clamp(28px, 8vw, 54px);
              background:
                linear-gradient(90deg, rgba(31, 78, 121, 0.08), transparent 18%, transparent 82%, rgba(31, 78, 121, 0.08)),
                linear-gradient(180deg, #fffaf0, #f6f4ef);
              box-shadow:
                0 30px 90px rgba(31, 78, 121, 0.16),
                inset 0 0 0 10px rgba(255, 250, 240, 0.66),
                inset 0 0 0 12px rgba(202, 162, 58, 0.16);
              isolation: isolate;
            }

            .entrance-stage::before,
            .entrance-stage::after {
              content: "";
              position: absolute;
              inset: 20px;
              border: 1px solid rgba(202, 162, 58, 0.35);
              border-radius: clamp(22px, 7vw, 42px);
              pointer-events: none;
              z-index: 3;
            }

            .entrance-stage::after {
              inset: 34px;
              border-color: rgba(31, 78, 121, 0.16);
            }

            .entrance-door {
              position: absolute;
              inset-block: 0;
              width: 51%;
              z-index: 1;
              background:
                radial-gradient(circle at 50% 18%, rgba(255, 250, 240, 0.14), transparent 26%),
                linear-gradient(90deg, rgba(255, 250, 240, 0.08), transparent 28%),
                linear-gradient(180deg, #1f4e79, #183d61 48%, #102c47);
              box-shadow: inset 0 0 0 1px rgba(255, 250, 240, 0.1);
              will-change: transform;
            }

            .entrance-door-left {
              left: 0;
              transform-origin: left center;
              animation: entrance-door-left 2.65s cubic-bezier(0.16, 1, 0.3, 1) 2s forwards;
            }

            .entrance-door-right {
              right: 0;
              transform-origin: right center;
              animation: entrance-door-right 2.65s cubic-bezier(0.16, 1, 0.3, 1) 2s forwards;
            }

            .entrance-door::before {
              content: "";
              position: absolute;
              inset: 7%;
              border: 1px solid rgba(241, 207, 122, 0.24);
              border-radius: 999px 999px 30px 30px;
            }

            .entrance-door-left::after,
            .entrance-door-right::after {
              content: "";
              position: absolute;
              top: 0;
              width: 18px;
              height: 100%;
              background: linear-gradient(180deg, rgba(241, 207, 122, 0.56), rgba(202, 162, 58, 0.18), rgba(241, 207, 122, 0.5));
            }

            .entrance-door-left::after {
              right: 0;
            }

            .entrance-door-right::after {
              left: 0;
            }

            .entrance-light {
              position: absolute;
              inset: 0;
              z-index: 0;
              display: grid;
              place-items: center;
              background:
                radial-gradient(circle at 50% 43%, rgba(241, 207, 122, 0.72), transparent 24%),
                radial-gradient(circle at 50% 44%, rgba(255, 250, 240, 0.94), transparent 42%),
                linear-gradient(180deg, #fffaf0, #f1e6c7);
              opacity: 0;
              animation: entrance-light-rise 2.25s ease 2.55s forwards;
            }

            .entrance-copy {
              position: absolute;
              inset: 0;
              z-index: 4;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 44px 28px;
              text-align: center;
              pointer-events: none;
            }

            .entrance-kicker {
              margin: 0;
              color: rgba(255, 250, 240, 0.86);
              font-size: clamp(0.8rem, 3vw, 0.95rem);
              font-weight: 800;
              letter-spacing: 0.28em;
              text-transform: uppercase;
              animation: entrance-copy-blue-to-navy 900ms ease 3.55s forwards;
            }

            .entrance-title {
              margin: 18px 0 0;
              color: #fffaf0;
              font-family: Georgia, "Times New Roman", serif;
              font-size: clamp(4.25rem, 19vw, 8rem);
              font-style: italic;
              font-weight: 500;
              line-height: 0.88;
              text-shadow:
                0 0 2px #f1cf7a,
                0 0 28px rgba(241, 207, 122, 0.55);
              animation: entrance-title-settle 4.4s ease forwards;
            }

            .entrance-names {
              margin: 24px 0 0;
              color: rgba(255, 250, 240, 0.9);
              font-size: clamp(1rem, 4.2vw, 1.35rem);
              letter-spacing: 0.22em;
              text-transform: uppercase;
              animation: entrance-copy-blue-to-navy 900ms ease 3.55s forwards;
            }

            .entrance-heart-wrap {
              position: relative;
              width: clamp(82px, 22vw, 120px);
              height: clamp(72px, 19vw, 104px);
              margin-top: 30px;
              filter: drop-shadow(0 0 18px rgba(241, 207, 122, 0.68));
              animation: entrance-heart-glow 1.35s ease-in-out infinite alternate;
            }

            .entrance-heart-half {
              position: absolute;
              top: 0;
              width: 50%;
              height: 100%;
              overflow: hidden;
              will-change: transform, opacity;
            }

            .entrance-heart-left {
              left: 0;
              animation: entrance-heart-left 1.3s cubic-bezier(0.16, 1, 0.3, 1) 1.15s forwards;
            }

            .entrance-heart-right {
              right: 0;
              animation: entrance-heart-right 1.3s cubic-bezier(0.16, 1, 0.3, 1) 1.15s forwards;
            }

            .entrance-heart-half::before {
              content: "♥";
              position: absolute;
              top: -0.18em;
              color: #f1cf7a;
              font-size: clamp(6.3rem, 27vw, 9.2rem);
              line-height: 1;
              text-shadow:
                0 0 12px rgba(241, 207, 122, 0.8),
                0 0 30px rgba(31, 78, 121, 0.4);
            }

            .entrance-heart-left::before {
              left: 0;
            }

            .entrance-heart-right::before {
              right: 0;
              color: #2f67aa;
            }

            .entrance-reveal-message {
              position: absolute;
              z-index: 2;
              max-width: 320px;
              padding: 0 24px;
              color: #1f3f6d;
              font-size: clamp(1rem, 4vw, 1.28rem);
              font-weight: 600;
              line-height: 1.55;
              opacity: 0;
              text-align: center;
              transform: translateY(18px);
              animation: entrance-message-in 1.1s ease 3.1s forwards;
            }

            .entrance-replay {
              position: fixed;
              bottom: max(14px, env(safe-area-inset-bottom));
              left: max(14px, env(safe-area-inset-left));
              z-index: 35;
              min-height: 42px;
              border: 1px solid rgba(202, 162, 58, 0.48);
              border-radius: 999px;
              background: rgba(255, 250, 240, 0.88);
              color: #1f4e79;
              cursor: pointer;
              font-size: 0.7rem;
              font-weight: 800;
              letter-spacing: 0.12em;
              padding: 0 14px;
              text-transform: uppercase;
              box-shadow: 0 10px 24px rgba(31, 78, 121, 0.1);
              backdrop-filter: blur(12px);
              -webkit-tap-highlight-color: transparent;
            }

            @keyframes entrance-heart-glow {
              from {
                filter: drop-shadow(0 0 12px rgba(241, 207, 122, 0.54));
                transform: scale(1);
              }
              to {
                filter: drop-shadow(0 0 28px rgba(241, 207, 122, 0.94));
                transform: scale(1.035);
              }
            }

            @keyframes entrance-heart-left {
              to {
                opacity: 0.94;
                transform: translate3d(-34px, -5px, 0) rotate(-9deg);
              }
            }

            @keyframes entrance-heart-right {
              to {
                opacity: 0.94;
                transform: translate3d(34px, -5px, 0) rotate(9deg);
              }
            }

            @keyframes entrance-door-left {
              to {
                transform: translate3d(-84%, 0, 0) rotateY(-16deg);
              }
            }

            @keyframes entrance-door-right {
              to {
                transform: translate3d(84%, 0, 0) rotateY(16deg);
              }
            }

            @keyframes entrance-light-rise {
              to {
                opacity: 1;
              }
            }

            @keyframes entrance-title-settle {
              0%,
              62% {
                color: #fffaf0;
              }
              100% {
                color: #1f4e79;
                text-shadow:
                  0 0 1px #f1cf7a,
                  0 0 16px rgba(241, 207, 122, 0.44);
              }
            }

            @keyframes entrance-copy-blue-to-navy {
              to {
                color: #1f3f6d;
              }
            }

            @keyframes entrance-message-in {
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes entrance-shell-fade {
              to {
                opacity: 0;
                pointer-events: none;
              }
            }

            @media (max-width: 420px) {
              .entrance-stage {
                width: min(94vw, 380px);
                height: min(76dvh, 610px);
                min-height: 480px;
              }

              .entrance-copy {
                padding-inline: 20px;
              }

              .entrance-heart-wrap {
                margin-top: 24px;
              }
            }

            @media (prefers-reduced-motion: reduce) {
              .wedding-entrance-overlay {
                animation: entrance-shell-fade 500ms ease 1s forwards;
              }

              .entrance-door,
              .entrance-heart-wrap,
              .entrance-heart-half,
              .entrance-light,
              .entrance-title,
              .entrance-kicker,
              .entrance-names,
              .entrance-reveal-message {
                animation-duration: 1ms !important;
                animation-delay: 1ms !important;
              }
            }
          `}</style>

          <button type="button" className="entrance-skip" onClick={completeEntrance}>
            Skip
          </button>

          <div className="entrance-stage" aria-hidden="true">
            <div className="entrance-light" />
            <div className="entrance-reveal-message">
              Opening the door to their new life together.
            </div>
            <div className="entrance-door entrance-door-left" />
            <div className="entrance-door entrance-door-right" />
            <div className="entrance-copy">
              <p className="entrance-kicker">Welcome to the wedding of</p>
              <h2 className="entrance-title">Mr &amp; Mrs</h2>
              <div className="entrance-heart-wrap">
                <span className="entrance-heart-half entrance-heart-left" />
                <span className="entrance-heart-half entrance-heart-right" />
              </div>
              <p className="entrance-names">John &amp; Maxine</p>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="entrance-replay"
        onClick={replayEntrance}
        aria-label="Replay entrance animation"
      >
        Replay Entrance
      </button>
    </>
  );
}
